import { forwardRef } from "react";
import { useCartStore } from "./cartStore";
import { centsFromYuanOrZero, formatYuan } from "./money";

interface Props {
  onOpenCart: () => void;
}

/**
 * 底部购物车条（基线 `shop/CartBar.tsx:16` 的 `h-16` + `border-t-2 border-black`）。
 *
 * ⚠️ **金额口径**（§7.8）：`formatYuan(centsFromYuanOrZero(...))` ⇒ `¥1,280.00`
 * （前缀 + 两位小数 + 千分位）。自己把浮点元拼成两位小数会显示 `¥1280.00`，是 Phase 4 链 E
 * 审出的用户可见项，Phase 5 Task 22 收口到 `packages/ui/src/money.ts` 的**唯一**实现。
 *
 * ⚠️ 这里显示的是**本店购物车的行金额合计**（`Σ price × quantity` 的浮点元）——
 * `packages/ui` 内没有服务端/促销价来源，所以它不是「实付金额」：方案促销的实付与划价
 * 在 `apps/shop` 的购物车页（`cart/cartView.ts` 的 `total.paidCents`，服务端口径）。
 * 两者不一致的可能已在 Phase 4 附录 C 登记（原价合计 vs 实付），**不是本文件能修的**：
 * 修它需要给本组件加一个「服务端/促销价合计」入参并由调用页接线。
 */
export default forwardRef<HTMLButtonElement, Props>(function CartBar({ onOpenCart }, ref) {
  const totalPrice = useCartStore((s) => s.totalPrice());
  const totalItems = useCartStore((s) => s.totalItems());

  return (
    <button
      ref={ref}
      onClick={onOpenCart}
      className="flex h-16 items-center justify-between border-t-2 border-black bg-white px-4 active:bg-gray-100"
    >
      <div className="flex items-center gap-2">
        <span className="text-2xl">🛒</span>
        <span id="cart-label" className="text-lg font-bold">
          购物车
        </span>
        {totalItems > 0 && (
          <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-black px-1 text-sm font-bold text-white">
            {totalItems}
          </span>
        )}
      </div>
      <span className="text-xl font-bold">{formatYuan(centsFromYuanOrZero(totalPrice))}</span>
    </button>
  );
});
